import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { TranslationService } from '@/layout/service/translation.service';
import { translateCount } from '@/layout/service/translate-count';
import { RoomFeedbackStats, feedbackTone } from '@/cloud/models/feedback.models';

/**
 * A room's average rating as a compact badge ("★ 4.2", with "· 12 responses" when `showCount`), coloured
 * poor / mixed / good. Renders "—" when the room has no feedback (or nothing in `empty` mode "hidden").
 */
@Component({
    selector: 'app-room-rating',
    standalone: true,
    imports: [DecimalPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (feedback(); as f) {
            <span class="room-rating" [attr.data-tone]="tone()" [title]="label()" role="img" [attr.aria-label]="label()">
                <i class="pi pi-star-fill" aria-hidden="true"></i>
                <strong>{{ f.average | number: '1.1-1' }}</strong>
                @if (showCount()) {
                    <span class="room-rating__count">· {{ responses() }}</span>
                }
            </span>
        } @else if (empty() === 'dash') {
            <span class="room-rating room-rating--none" [title]="t()('room.snippet.noFeedback')">—</span>
        }
    `,
    styles: [
        `
            :host {
                display: inline-flex;
                min-width: 0;
            }
            .room-rating {
                display: inline-flex;
                align-items: center;
                gap: 0.3rem;
                white-space: nowrap;
                font-variant-numeric: tabular-nums;
                color: var(--text-color-secondary);
            }
            .room-rating i {
                font-size: 0.8em;
            }
            .room-rating strong {
                color: var(--text-color);
                font-weight: 600;
            }
            .room-rating__count {
                font-size: 0.9em;
            }
            .room-rating[data-tone='poor'] i,
            .room-rating[data-tone='poor'] strong {
                color: var(--app-status-offline-fg);
            }
            .room-rating[data-tone='mixed'] i {
                color: var(--app-status-warning-fg);
            }
            .room-rating[data-tone='good'] i {
                color: var(--app-status-online-fg);
            }
            .room-rating--none {
                color: var(--text-color-secondary);
            }
        `
    ]
})
export class RoomRatingComponent {
    readonly t = inject(TranslationService).t;

    readonly feedback = input<RoomFeedbackStats | null>(null);
    readonly showCount = input(false);
    /** Without feedback: a dash (tables) or nothing (cards). */
    readonly empty = input<'dash' | 'hidden'>('dash');

    readonly tone = computed(() => feedbackTone(this.feedback()?.average));
    readonly responses = computed(() => translateCount(this.t(), 'room.snippet.responses', this.feedback()?.count ?? 0));
    /** "Average 4.2 / 5 from 12 responses in the last 30 days". */
    readonly label = computed(() => {
        const f = this.feedback();
        if (!f) return '';
        return translateCount(this.t(), 'roomFeedback.ratingLabel', f.count, { avg: f.average.toFixed(1) });
    });
}
